import { memo, useCallback, useEffect, useMemo } from 'react';
import { Pressable } from 'react-native';
import {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { StackNavigationProp, useNavigation } from '@bonanza-social/navigation';
import { formatNumber } from '@bonanza-social/toolkit/utils';
import { Box, Icon, Text } from '@bonanza-social/moonlight';
import { useSelector } from '@bonanza-social/toolkit/state';
import { selectCommunityJackpotEvents } from '../../state';
import { getActiveMilestone } from '../../utils/jackpot';
import { CommunityJackpotTrackerProps } from './CommunityJackpotTracker.types';

function CommunityJackpotTracker({
  onMilestoneHit,
  ...rest
}: CommunityJackpotTrackerProps) {
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const [event] = useSelector(selectCommunityJackpotEvents);
  const { totalContributions } = event;
  const activeMilestone = useMemo(() => getActiveMilestone(event), [event]);
  const milestoneHit =
    totalContributions >=
    (activeMilestone?.threshold || Number.MAX_SAFE_INTEGER);
  const scale = useSharedValue(1);
  const opacity = useSharedValue(0);
  const animatedStyle = {
    container: useAnimatedStyle(() => ({
      opacity: opacity.value,
    })),
    child: useAnimatedStyle(() => ({
      transform: [{ scale: scale.value }],
    })),
  };

  const onPress = useCallback(() => {
    navigation.navigate('CommunityJackpot');
  }, [navigation]);

  useEffect(() => {
    if (milestoneHit) {
      opacity.value = withTiming(1, { duration: 300 });

      scale.value = withRepeat(
        withSequence(
          withTiming(1.0, { duration: 300 }),
          withTiming(1.1, { duration: 300 }),
          withTiming(1.1, { duration: 300 }),
          withTiming(1.15, { duration: 300 }),
          withTiming(1, { duration: 300 }),
        ),
        4,
        false,
        () => {
          onMilestoneHit();

          opacity.value = withTiming(0, { duration: 300 });
        },
      );
    }
  }, [milestoneHit, opacity, scale, onMilestoneHit]);

  return (
    <Box
      paddingVertical="space-200"
      paddingHorizontal="space-300"
      backgroundColor="mutedBackground"
      borderRadius="radius-200"
      overflow="hidden"
      {...rest}
    >
      <Pressable onPress={onPress}>
        <Box flexDirection="row" alignItems="center" gap="space-100">
          <Icon
            name="bag"
            color="iconSecondary"
            width="space-300"
            height="space-300"
          />
          <Text
            variant="nav-label"
            locKey="tracker"
            locNS="communityJackpot"
            color="darkSurfacePrimary"
          />
        </Box>
        <Text variant="badge" color="darkSurfacePrimary" paddingTop="space-050">
          {formatNumber(totalContributions, {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
          })}
        </Text>
      </Pressable>
      <Box
        position="absolute"
        backgroundColor="mutedBackground"
        pointerEvents="none"
        animatedStyle={animatedStyle.container}
        centerContent
        inset={0}
      >
        <Box animatedStyle={animatedStyle.child}>
          <Text
            variant="caption"
            locKey="trackerMilestone"
            locNS="communityJackpot"
            color="darkSurfacePrimary"
          />
          <Text
            variant="caption"
            locKey="trackerMilestoneReached"
            locNS="communityJackpot"
            color="darkSurfacePrimary"
          />
        </Box>
      </Box>
    </Box>
  );
}

export default memo(CommunityJackpotTracker);
